<template>
  <div v-if="loaded && (tasks.length || canAdd)" class="flex flex-col gap-3" dir="rtl">
    <div v-for="phase in phases" :key="phase.key" class="flex flex-col">
      <div class="flex justify-between items-center mb-1">
        <span class="text-sm font-bold text-surface-900">{{ phase.label }}</span>
        <span class="text-xs text-surface-500">{{ phase.doneCount }} מתוך {{ phase.tasks.length }}</span>
      </div>
      <div
        v-for="task in phase.tasks"
        :key="task.id"
        class="flex items-center gap-2.5 min-h-[44px] border-b border-surface-100 last:border-b-0"
      >
        <TaskCheckbox :checked="isDone(task)" :disabled="busyId === task.id" @toggle="toggle(task)" />
        <div class="flex-1 min-w-0 flex flex-col">
          <span class="text-[15px]" :class="{ 'line-through text-surface-400': isDone(task) }">{{ task.title }}</span>
          <span v-if="followUpNote(task)" class="text-xs text-surface-500">{{ followUpNote(task) }}</span>
        </div>
        <span v-if="task.autoType" class="text-[11px] text-surface-500 flex-none">אוטומטי</span>
      </div>
    </div>

    <div v-if="!tasks.length" class="text-sm text-surface-500">
      אין משימות לתור הזה. אפשר להגדיר רשימה קבועה לכל שירות בעריכת השירות.
    </div>

    <button v-if="canAdd" type="button" class="add-btn" @click="add">
      <i class="pi pi-plus text-xs" /> משימה לתור
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { type ITask, TaskChecklistPhaseEnum, TaskStatusEnum } from '@torim-app/types';
import { useAdminTasksStore } from '@app/stores';
import { TasksApi } from '@app/api/admin/tasks-api';
import TaskCheckbox from './TaskCheckbox.vue';
import { dueDayLabel, isDone } from './task-utils';
import { useTaskEditor } from './use-task-editor';

const props = defineProps<{
  appointmentId: string;
  /** yyyy-MM-dd of the appointment — new tasks default to it. */
  appointmentDate?: string;
  customerId?: string;
  customerName?: string;
  canAdd?: boolean;
}>();

const emit = defineEmits<{ progress: [done: number, total: number] }>();

const tasksStore = useAdminTasksStore();
const toast = useToast();
const { openTaskEditor } = useTaskEditor();

const tasks = ref<ITask[]>([]);
const loaded = ref(false);
const busyId = ref<string>();

const phases = computed(() => {
  // Tasks added by hand to the appointment count as prep.
  const before = tasks.value.filter(t => t.checklistPhase !== TaskChecklistPhaseEnum.After);
  const after = tasks.value.filter(t => t.checklistPhase === TaskChecklistPhaseEnum.After);
  return [
    { key: 'before', label: 'לפני הטיפול', tasks: before },
    { key: 'after', label: 'אחרי הטיפול', tasks: after },
  ]
    .filter(p => p.tasks.length)
    .map(p => ({ ...p, doneCount: p.tasks.filter(isDone).length }));
});

const followUpNote = (task: ITask) => {
  if (task.checklistPhase !== TaskChecklistPhaseEnum.After || !task.dueDate || task.dueDate === props.appointmentDate) return '';
  return isDone(task) ? '' : `משימת המשך · ${dueDayLabel(task.dueDate)}`;
};

const load = async () => {
  try {
    tasks.value = await TasksApi.listForAppointment(props.appointmentId);
    emit('progress', tasks.value.filter(isDone).length, tasks.value.length);
  } catch (e) {
    console.error(e);
  } finally {
    loaded.value = true;
  }
};

const toggle = async (task: ITask) => {
  if (!task.id || busyId.value) return;
  busyId.value = task.id;
  const done = task.status !== TaskStatusEnum.Done;
  task.status = done ? TaskStatusEnum.Done : TaskStatusEnum.Open;
  try {
    await tasksStore.setDone(task.id, done);
  } catch (e) {
    console.error(e);
    task.status = done ? TaskStatusEnum.Open : TaskStatusEnum.Done;
    toast.error('לא הצלחנו לעדכן את המשימה');
  } finally {
    busyId.value = undefined;
  }
};

const add = () => openTaskEditor({
  defaults: {
    appointmentId: props.appointmentId,
    dueDate: props.appointmentDate,
    customerId: props.customerId,
    customerName: props.customerName,
  },
  lockCustomer: true,
});

watch(() => props.appointmentId, load, { immediate: true });
watch(() => tasksStore.version, load);
</script>

<style scoped lang="scss">
.add-btn {
  @apply h-10 rounded-[10px] text-[13px] font-semibold text-primary-700 bg-white flex items-center justify-center gap-1.5;
  border: 1.5px dashed #b6a8e6;
}
</style>
